import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Dashboard from './components/Dashboard';
import Login from './pages/Login';
import './App.css';

// Create a custom theme matching Circle Exchange branding
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#B8860B', // Dark goldenrod - matching logo gold
      light: '#DAA520', // Goldenrod
      dark: '#8B6914', // Darker gold
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2C2C2C', // Dark gray from logo
      light: '#424242',
      dark: '#1A1A1A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#FAFAFA',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C2C2C',
      secondary: '#666666',
    },
    success: {
      main: '#4CAF50',
    },
    error: {
      main: '#F44336',
    },
    warning: {
      main: '#FF9800',
    },
    info: {
      main: '#2196F3',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      fontSize: '2.5rem',
      color: '#2C2C2C',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2rem',
      color: '#2C2C2C',
    },
    h3: {
      fontWeight: 600,
      fontSize: '1.75rem',
      color: '#2C2C2C',
    },
    h4: {
      fontWeight: 600,
      fontSize: '1.5rem',
      color: '#2C2C2C',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.25rem',
      color: '#2C2C2C',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.125rem',
      color: '#2C2C2C',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 500,
          padding: '8px 16px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(184, 134, 11, 0.3)',
          },
        },
        contained: {
          background: 'linear-gradient(135deg, #B8860B 0%, #DAA520 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #8B6914 0%, #B8860B 100%)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid rgba(184, 134, 11, 0.1)',
        },
        elevation1: {
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
        },
        elevation2: {
          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          background: 'linear-gradient(135deg, #2C2C2C 0%, #1A1A1A 100%)',
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.15)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#FAFAFA',
          borderRight: '1px solid rgba(184, 134, 11, 0.2)',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          margin: '2px 8px',
          '&.Mui-selected': {
            backgroundColor: 'rgba(184, 134, 11, 0.1)',
            borderLeft: '4px solid #B8860B',
            '&:hover': {
              backgroundColor: 'rgba(184, 134, 11, 0.15)',
            },
          },
          '&:hover': {
            backgroundColor: 'rgba(184, 134, 11, 0.05)',
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: 'rgba(184, 134, 11, 0.08)',
            fontWeight: 600,
            color: '#2C2C2C',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: 'rgba(184, 134, 11, 0.04)',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
        colorPrimary: {
          backgroundColor: 'rgba(184, 134, 11, 0.1)',
          color: '#B8860B',
          border: '1px solid rgba(184, 134, 11, 0.3)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#B8860B',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#B8860B',
            },
          },
          '& .MuiInputLabel-root.Mui-focused': {
            color: '#B8860B',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          border: '1px solid rgba(184, 134, 11, 0.2)',
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router>
          <div className="App">
            <Routes>
              {/* Login Route */}
              <Route path="/login" element={<Login />} />
              
              {/* Protected Dashboard Routes */}
              <Route
                path="/dashboard/*"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Navigate to="/dashboard" replace />
                  </ProtectedRoute>
                }
              />
              
              {/* Catch all route - redirect to dashboard */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </div>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
